import { computed, ref } from '@vue/composition-api'
import store from '@/store'
import { apiToastErrorV2, apiToastSuccessV2 } from '@/@core/utils/toast'
import {
  FETCH_WISHLIST_PRICED_PREVIEW_BLOCK,
  UPDATE_WISHLIST_PREVIEW_SETTINGS,
} from '@/store/modules/shop.module'

// Mirrors catalog-service's grid: 6 tiles per row, at most 3 rows. The chosen
// products ARE the quantity — there is no separate count, because when the
// brand picks by hand a count can only restate how many they picked.
export const PREVIEW_ROW_CAPACITY = 6
export const MAX_PREVIEW_PRODUCTS = 18
export const DEFAULT_PREVIEW_PRODUCTS = 6

// What a recipient sees when the brand has expressed no preference. Wholesale
// is hidden: a buyer must never be shown the brand's cost by default.
const SURFACE_DEFAULTS = { wholesale: false, retail: true }

/**
 * Drives the wishlist's price-visibility toggles and email-preview selection
 * (KP-3861).
 *
 * The important subtlety is what gets SAVED. `priceVisibility` is a tri-state
 * server-side — absent means "no preference", and each surface then applies its
 * own default. A checkbox has no third state, so the composable tracks which
 * toggles the brand actually touched and sends only those. Persisting the
 * displayed value instead would write `wholesale: false` for a brand that
 * merely opened the dialog, and that explicit false would strip WSP from the
 * retailer's copy of every wishlist they have already shared.
 */
export function useWishlistPreviewSettings(wishlistRef) {
  const saving = ref(false)
  const loadingPreview = ref(false)
  const previewHtml = ref('')
  const previewError = ref(null)

  const wholesale = ref(SURFACE_DEFAULTS.wholesale)
  const retail = ref(SURFACE_DEFAULTS.retail)
  const touched = ref({ wholesale: false, retail: false })

  const selectedIds = ref([])

  /** Seeds the form from the wishlist, leaving unset flags at their default. */
  function hydrate() {
    const wishlist = wishlistRef?.value || {}
    const visibility = wishlist.priceVisibility || {}

    wholesale.value = typeof visibility.wholesale === 'boolean'
      ? visibility.wholesale
      : SURFACE_DEFAULTS.wholesale
    retail.value = typeof visibility.retail === 'boolean'
      ? visibility.retail
      : SURFACE_DEFAULTS.retail
    touched.value = { wholesale: false, retail: false }

    selectedIds.value = [...(wishlist.previewProductIds || [])]
    previewHtml.value = ''
    previewError.value = null
  }

  function togglePrice(kind) {
    if (kind === 'wholesale') wholesale.value = !wholesale.value
    else retail.value = !retail.value
    touched.value = { ...touched.value, [kind]: true }
  }

  /**
   * Adds or removes a product, preserving click order — the order the brand
   * picks in IS the order the email renders.
   */
  function toggleProduct(productId) {
    const index = selectedIds.value.indexOf(productId)
    if (index >= 0) {
      selectedIds.value = selectedIds.value.filter(id => id !== productId)
      return
    }
    if (selectedIds.value.length >= MAX_PREVIEW_PRODUCTS) {
      apiToastErrorV2(`The email fits ${MAX_PREVIEW_PRODUCTS} products. Remove one to add another.`)
      return
    }
    selectedIds.value = [...selectedIds.value, productId]
  }

  function moveProduct(productId, direction) {
    const ids = [...selectedIds.value]
    const from = ids.indexOf(productId)
    const to = from + direction
    if (from < 0 || to < 0 || to >= ids.length) return
    ids.splice(to, 0, ids.splice(from, 1)[0])
    selectedIds.value = ids
  }

  const rowCount = computed(() => Math.ceil(
    (selectedIds.value.length || DEFAULT_PREVIEW_PRODUCTS) / PREVIEW_ROW_CAPACITY,
  ))

  /** Only touched price flags travel — see the note on the composable. */
  function buildPayload() {
    const payload = { previewProductIds: selectedIds.value }
    const visibility = {}
    if (touched.value.wholesale) visibility.wholesale = wholesale.value
    if (touched.value.retail) visibility.retail = retail.value
    if (Object.keys(visibility).length) payload.priceVisibility = visibility

    return payload
  }

  async function save() {
    saving.value = true
    try {
      await store.dispatch(UPDATE_WISHLIST_PREVIEW_SETTINGS, {
        wishlistId: wishlistRef.value?._id,
        payload: buildPayload(),
      })
      touched.value = { wholesale: false, retail: false }
      apiToastSuccessV2('Preview settings saved')
      return true
    } catch (err) {
      apiToastErrorV2('Couldn’t save your preview settings. Please try again.')
      return false
    } finally {
      saving.value = false
    }
  }

  function applyPreview(html) {
    previewHtml.value = html || ''
    previewError.value = previewHtml.value
      ? null
      : 'This wishlist has no products to preview yet.'
  }

  /**
   * Loads the block exactly as the email will render it.
   *
   * Keyed by wishlist, not by a public link token. Tokens live on publicLinks
   * and are minted on demand, so a token-keyed preview would either tell the
   * brand to share the wishlist first or mint a live public URL as a side
   * effect of pressing Preview.
   *
   * Reads STORED settings, so the caller saves first — otherwise the brand is
   * shown the previous state and reasonably concludes their edit was lost.
   */
  async function loadPreview() {
    const wishlistId = wishlistRef.value?._id
    if (!wishlistId) {
      previewError.value = 'Open a saved wishlist to preview its email.'
      return
    }
    loadingPreview.value = true
    previewError.value = null
    try {
      // KP-4586: priced like the public link, so a price book shows here too.
      const res = await store.dispatch(FETCH_WISHLIST_PRICED_PREVIEW_BLOCK, { wishlistId })
      applyPreview(res?.data?.response?.html)
    } catch (err) {
      previewError.value = 'Couldn’t load the preview. Please try again.'
    } finally {
      loadingPreview.value = false
    }
  }

  return {
    saving,
    loadingPreview,
    previewHtml,
    previewError,
    wholesale,
    retail,
    selectedIds,
    rowCount,
    hydrate,
    togglePrice,
    toggleProduct,
    moveProduct,
    buildPayload,
    save,
    loadPreview,
  }
}
